import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SITE_URL } from "@/lib/seo";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronRight, Calendar, Clock, Link as LinkIcon, ArrowRight,
  Copy, User, ChevronDown, MessageCircle, Twitter, Linkedin, Facebook,
} from "lucide-react";
import { BlockRenderer, extractTOC, slugFor } from "@/components/BlockRenderer";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setNotFound(false); setData(null);
    api.get(`/public/blog/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const toc = useMemo(() => extractTOC(data?.post?.body), [data]);

  // scroll spy
  useEffect(() => {
    if (toc.length === 0) return;
    const handler = () => {
      const scrollY = window.scrollY + 180;
      let current = toc[0]?.id || "";
      for (const item of toc) {
        const el = document.getElementById(item.id);
        if (el && el.offsetTop <= scrollY) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [toc]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-bold text-3xl text-[#0f1b2d]">Article not found</h1>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#2563eb] font-semibold">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="h-4 w-40 bg-[#f1f5f9] rounded animate-pulse" />
          <div className="mt-4 h-12 w-full bg-[#f1f5f9] rounded animate-pulse" />
          <div className="mt-8 h-96 w-full bg-[#f1f5f9] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const p = data.post;
  const author = data.author;
  const related = data.related || [];
  const isNews = p.is_news;
  const catLabel = categoryLabel(p.category);
  const publicUrl = `${SITE_URL}/blog/${p.slug}`;
  const showUpdated = p.updated_at && p.published_at && p.updated_at.slice(0, 10) !== p.published_at.slice(0, 10);

  const share = {
    copy: async () => { try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); } catch { toast.error("Copy failed"); } },
    whatsapp: () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(p.title + " " + publicUrl)}`, "_blank"),
    twitter: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(p.title)}&url=${encodeURIComponent(publicUrl)}`, "_blank"),
    linkedin: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`, "_blank"),
    facebook: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`, "_blank"),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: catLabel, item: `${SITE_URL}/blog?category=${p.category}` },
      { "@type": "ListItem", position: 4, name: p.title, item: publicUrl },
    ],
  };
  const articleLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: p.title,
    image: p.featured_image ? [p.featured_image] : [`${SITE_URL}/og-image.png`],
    datePublished: p.published_at,
    dateModified: p.updated_at,
    author: author ? { "@type": "Person", name: author.name, jobTitle: author.title } : { "@type": "Organization", name: "SchengenFile" },
    publisher: { "@type": "Organization", name: "SchengenFile", logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.png` } },
    description: p.meta_description || p.excerpt,
    mainEntityOfPage: publicUrl,
  };
  // Also extract FAQ blocks for FAQPage schema
  const faqBlocks = (p.body || []).filter((b) => b.type === "faq");
  const faqLd = faqBlocks.length > 0 ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqBlocks.flatMap((b) => (b.items || []).map((it) => ({
      "@type": "Question", name: it.q, acceptedAnswer: { "@type": "Answer", text: it.a },
    }))),
  } : null;
  const jsonLd = [breadcrumbLd, articleLd, ...(faqLd ? [faqLd] : [])];

  return (
    <div className="min-h-screen bg-white text-[#0f1b2d]" data-testid="blog-post-page">
      <SEO
        title={p.meta_title || p.title}
        description={p.meta_description || p.excerpt || ""}
        path={p.canonical_url || `/blog/${p.slug}`}
        image={p.featured_image || undefined}
        type="article"
        noindex={p.no_index}
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        <section className="pt-8 pb-6 border-b border-[#e2e8f0]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <nav className="text-xs text-[#64748b] flex items-center gap-1.5 flex-wrap" data-testid="post-breadcrumb">
              <Link to="/" className="hover:text-[#0f1b2d]">Home</Link><ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-[#0f1b2d]">Blog</Link><ChevronRight className="w-3 h-3" />
              <Link to={`/blog?category=${p.category}`} className="hover:text-[#0f1b2d]">{catLabel}</Link><ChevronRight className="w-3 h-3" />
              <span className="text-[#0f1b2d] truncate">{p.title}</span>
            </nav>

            <div className="mt-4 flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${isNews ? "bg-[#dc2626] text-white" : "bg-[#eff6ff] text-[#2563eb]"}`}>
                {isNews ? "News" : catLabel}
              </span>
              {isNews && (
                <span className="text-xs text-[#dc2626] font-semibold inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {formatDate(p.published_at, true)}
                </span>
              )}
            </div>
            <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#0f1b2d]" data-testid="post-h1">
              {p.title}
            </h1>
            <div className="mt-5 flex items-center gap-3 flex-wrap text-sm text-[#64748b]">
              {author && (
                <div className="flex items-center gap-2">
                  {author.avatar_url ? (
                    <img src={author.avatar_url} alt={author.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#dbeafe] grid place-items-center"><User className="w-4 h-4 text-[#2563eb]" /></div>
                  )}
                  <span className="text-[#0f1b2d] font-medium">{author.name}</span>
                </div>
              )}
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(p.published_at)}</span>
              {showUpdated && <span className="text-[#94a3b8]">· Updated {formatDate(p.updated_at)}</span>}
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{p.reading_time || 5} min read</span>
            </div>

            {p.featured_image && (
              <img src={p.featured_image} alt={p.title} className="mt-6 w-full aspect-[16/9] object-cover rounded-xl border border-[#e2e8f0]" />
            )}
          </div>
        </section>

        {/* Body + sidebar layout */}
        <section className="py-10">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-10">
            <article className="max-w-[720px] lg:max-w-none" data-testid="post-body">
              {/* Mobile TOC */}
              {toc.length > 1 && (
                <details className="lg:hidden mb-6 rounded-lg border border-[#e2e8f0] bg-[#f8fafc]" data-testid="post-toc-mobile">
                  <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between font-semibold text-sm">
                    Contents <ChevronDown className="w-4 h-4" />
                  </summary>
                  <ul className="px-4 pb-4 text-sm space-y-1.5">
                    {toc.map((t) => (
                      <li key={t.id} className={t.level === 3 ? "pl-4" : ""}>
                        <a href={`#${t.id}`} className="text-[#475569] hover:text-[#2563eb]">{t.text}</a>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Share row */}
              <div className="mb-6 flex items-center gap-2" data-testid="post-share">
                <span className="text-xs text-[#64748b] font-semibold uppercase tracking-wider mr-1">Share</span>
                <ShareBtn label="Copy" onClick={share.copy}><Copy className="w-3.5 h-3.5" /></ShareBtn>
                <ShareBtn label="WhatsApp" onClick={share.whatsapp}><MessageCircle className="w-3.5 h-3.5" /></ShareBtn>
                <ShareBtn label="X" onClick={share.twitter}><Twitter className="w-3.5 h-3.5" /></ShareBtn>
                <ShareBtn label="LinkedIn" onClick={share.linkedin}><Linkedin className="w-3.5 h-3.5" /></ShareBtn>
                <ShareBtn label="Facebook" onClick={share.facebook}><Facebook className="w-3.5 h-3.5" /></ShareBtn>
              </div>

              <BlockRenderer blocks={p.body} />

              {/* Tags */}
              {p.tags && p.tags.length > 0 && (
                <div className="mt-10 pt-6 border-t border-[#e2e8f0] flex flex-wrap gap-2" data-testid="post-tags">
                  {p.tags.map((t) => (
                    <span key={t} className="text-xs bg-[#f1f5f9] text-[#475569] px-2.5 py-1 rounded-full">#{t}</span>
                  ))}
                </div>
              )}

              {/* Author box */}
              {author && (
                <div className="mt-10 rounded-xl border border-[#e2e8f0] bg-white p-5" data-testid="post-author-box">
                  <div className="flex items-start gap-4">
                    {author.avatar_url ? (
                      <img src={author.avatar_url} alt={author.name} className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#dbeafe] grid place-items-center flex-shrink-0"><User className="w-6 h-6 text-[#2563eb]" /></div>
                    )}
                    <div>
                      <div className="font-display font-bold text-[#0f1b2d]">{author.name}</div>
                      {author.title && <div className="text-xs text-[#64748b]">{author.title}</div>}
                      {author.bio && <p className="mt-2 text-sm text-[#475569] leading-relaxed">{author.bio}</p>}
                      <div className="mt-2 text-xs text-[#94a3b8]">Reviewed by the SchengenFile editorial team.</div>
                    </div>
                  </div>
                </div>
              )}
            </article>

            {/* Sticky TOC — desktop only */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                {toc.length > 1 && (
                  <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-4" data-testid="post-toc-desktop">
                    <div className="text-xs uppercase tracking-widest text-[#64748b] font-semibold mb-3">Contents</div>
                    <ul className="space-y-1.5 text-sm">
                      {toc.map((t) => (
                        <li key={t.id} className={t.level === 3 ? "pl-3" : ""}>
                          <a
                            href={`#${t.id}`}
                            className={`block truncate transition ${activeId === t.id ? "text-[#2563eb] font-semibold" : "text-[#475569] hover:text-[#0f1b2d]"}`}
                          >
                            {t.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>

        {/* End-of-post CTA */}
        <section className="py-14 bg-gradient-to-br from-[#0f1b2d] to-[#1e3a8a] text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h3 className="font-display font-bold text-2xl sm:text-3xl">Get your Schengen visa documents prepared in minutes</h3>
            <p className="mt-3 text-white/75 max-w-xl mx-auto">Flight PNRs, hotel bookings, cover letters and insurance certificates — all consulate-ready, delivered in 24 hours.</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm">
              <div className="px-3 py-1.5 bg-white/10 rounded-md">Basic $39</div>
              <div className="px-3 py-1.5 bg-white/10 rounded-md">Complete $69</div>
              <div className="px-3 py-1.5 bg-white/10 rounded-md">Premium $99</div>
            </div>
            <Link to="/apply" data-testid="post-cta-apply" className="mt-6 inline-flex items-center gap-2 bg-white text-[#0f1b2d] font-semibold px-6 py-3 rounded-md">
              Start your file <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="py-14 border-t border-[#e2e8f0]">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
              <h3 className="font-display font-bold text-xl text-[#0f1b2d]">Related articles</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="post-related">
                {related.map((r) => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="rounded-lg border border-[#e2e8f0] bg-white p-4 hover:border-[#2563eb] transition">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#2563eb]">{categoryLabel(r.category)}</div>
                    <div className="mt-1 font-display font-bold text-[#0f1b2d] leading-snug line-clamp-2">{r.title}</div>
                    {r.excerpt && <p className="mt-2 text-xs text-[#64748b] line-clamp-2">{r.excerpt}</p>}
                    <div className="mt-3 text-xs text-[#64748b]">{formatDate(r.published_at)}</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function ShareBtn({ label, onClick, children }) {
  return (
    <button
      onClick={onClick}
      aria-label={`Share on ${label}`}
      data-testid={`post-share-${label.toLowerCase()}`}
      className="inline-flex items-center gap-1 text-xs text-[#475569] hover:text-[#2563eb] bg-white border border-[#e2e8f0] hover:border-[#2563eb] rounded-md px-2.5 py-1.5 transition"
    >
      {children}
      <span>{label}</span>
    </button>
  );
}
function categoryLabel(id) {
  return ({ country_guides: "Country Guides", document_tips: "Document Tips", visa_news: "Visa News", application_process: "Application Process" })[id] || id;
}
function formatDate(iso, full = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", full
      ? { weekday: "short", year: "numeric", month: "long", day: "numeric" }
      : { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}
